import { useDocumentCategories } from "@/hooks/use-document-categories";
import type { Id } from "@/packages/types";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, FolderOpen, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCategoryLucideIcon } from "./category-icon-map";
import { useDocumentCategoryLabel } from "./use-document-category-label";

interface CategoryPickerProps {
  selectedCategoryId?: Id<"documentCategories">;
  onSelect: (categoryId: Id<"documentCategories">) => void;
  disabled?: boolean;
}

export function CategoryPicker({
  selectedCategoryId,
  onSelect,
  disabled = false,
}: CategoryPickerProps) {
  const { t } = useTranslation("documents");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { categories, isLoading } = useDocumentCategories();
  const getCategoryLabel = useDocumentCategoryLabel();

  const selectedCategory = useMemo(
    () => categories.find((c) => c._id === selectedCategoryId),
    [categories, selectedCategoryId],
  );

  const filteredCategories = useMemo(() => {
    if (!search) return categories;
    const lowerSearch = search.toLowerCase();
    return categories.filter((c) => {
      const label = getCategoryLabel({ name: c.name, slug: c.slug });
      return (
        c.name.toLowerCase().includes(lowerSearch) ||
        label.toLowerCase().includes(lowerSearch)
      );
    });
  }, [categories, search, getCategoryLabel]);

  const handleSelect = useCallback(
    (categoryId: Id<"documentCategories">) => {
      onSelect(categoryId);
      setOpen(false);
      setSearch("");
      setActiveIndex(-1);
    },
    [onSelect],
  );

  // Reset active index when filtered list changes
  useEffect(() => {
    setActiveIndex(-1);
  }, []);

  // Focus input when popover opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    setSearch("");
    setActiveIndex(-1);
  }, [open]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll("[data-category-item]");
    items[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const count = filteredCategories.length;
    if (count === 0) return;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        setActiveIndex((prev) => (prev < count - 1 ? prev + 1 : 0));
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : count - 1));
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < count) {
          handleSelect(filteredCategories[activeIndex]._id);
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        setOpen(false);
        break;
      }
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[12px] font-medium transition-colors cursor-pointer ${
            selectedCategory
              ? "border-border bg-card text-secondary hover:border-border/80 hover:text-primary"
              : "border-border bg-card text-secondary hover:border-border/80 hover:text-primary"
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {selectedCategory ? (
            <>
              {(() => {
                const IconComponent = getCategoryLucideIcon(
                  selectedCategory.icon,
                );
                return IconComponent ? (
                  <IconComponent className="h-3.5 w-3.5 shrink-0 text-muted/70" />
                ) : (
                  <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted/70" />
                );
              })()}
              <span className="truncate">
                {getCategoryLabel({
                  name: selectedCategory.name,
                  slug: selectedCategory.slug,
                })}
              </span>
            </>
          ) : (
            <>
              <FolderOpen className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{t("upload.selectCategory")}</span>
            </>
          )}
          <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="w-[260px] bg-card rounded-xl border border-border/80 shadow-lg z-50"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onKeyDown={handleKeyDown}
          onWheel={(e) => e.stopPropagation()}
        >
          {/* Search input */}
          <div className="p-2.5 border-b border-border/40 flex items-center gap-2">
            <Search size={13} className="text-muted/70 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("upload.searchCategories")}
              className="w-full bg-transparent text-[13px] text-primary placeholder:text-muted/50 outline-none"
            />
          </div>

          {/* Options list */}
          <div
            ref={listRef}
            role="listbox"
            className="max-h-[240px] overflow-y-auto overscroll-contain p-1"
          >
            {isLoading ? (
              <div className="py-4 text-center text-[12px] text-muted/50">
                {t("upload.loadingCategories")}
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="p-4 text-center text-[13px] text-muted/60">
                {search
                  ? t("upload.noCategoriesFound")
                  : t("upload.noCategories")}
              </div>
            ) : (
              filteredCategories.map((category, index) => {
                const IconComponent = getCategoryLucideIcon(category.icon);
                const isSelected = category._id === selectedCategoryId;
                const isActive = index === activeIndex;

                return (
                  <button
                    key={category._id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-category-item
                    onClick={() => handleSelect(category._id)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] rounded-lg transition-colors cursor-pointer text-left ${
                      isActive
                        ? "bg-page/60 text-primary"
                        : isSelected
                          ? "bg-accent/10 text-primary"
                          : "text-primary/80 hover:text-primary hover:bg-page/60"
                    }`}
                  >
                    {IconComponent ? (
                      <IconComponent className="h-3.5 w-3.5 shrink-0 text-muted/70" />
                    ) : (
                      <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted/70" />
                    )}
                    <span className="flex-1 truncate">
                      {getCategoryLabel({
                        name: category.name,
                        slug: category.slug,
                      })}
                    </span>
                    {isSelected && (
                      <Check size={13} className="shrink-0 text-accent" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
